import { useMemo, useState } from "react";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import WorkCard from "@/components/feature/WorkCard";
import Pagination from "@/components/base/Pagination";
import EmptyState from "@/components/base/EmptyState";
import SortSelect from "@/components/base/SortSelect";
import WorkFilters, { type WorkFilterState } from "@/pages/work-list/components/WorkFilters";
import useSEO from "@/hooks/useSEO";
import { works } from "@/mocks/works";
import { getActressById, getActressNames } from "@/utils/data";

const PAGE_SIZE = 12;

const SORT_OPTIONS = [
  { value: "release", label: "上映时间（新→旧）" },
  { value: "rating", label: "评分（高→低）" },
  { value: "views", label: "热度（高→低）" },
];

const TYPE_OPTIONS = ["电影", "电视剧", "OVA", "网络剧"];
const YEARS = Array.from(new Set(works.map((item) => item.year))).sort((a, b) => b - a);

export default function WorkListPage() {
  const [filters, setFilters] = useState<WorkFilterState>({
    types: [],
    years: [],
    actressIds: [],
  });
  const [sort, setSort] = useState("release");
  const [page, setPage] = useState(1);
  const [mobileOpen, setMobileOpen] = useState(false);

  const actressOptions = useMemo(() => {
    const map = new Map<string, { name: string; count: number }>();
    works.forEach((work) => {
      work.actressIds.forEach((id) => {
        const actress = getActressById(id);
        if (!actress) return;
        const current = map.get(id);
        map.set(id, { name: actress.name, count: (current?.count ?? 0) + 1 });
      });
    });
    return Array.from(map.entries())
      .map(([value, info]) => ({ value, label: info.name, count: info.count }))
      .sort((a, b) => a.label.localeCompare(b.label, "zh-Hans-CN"));
  }, []);

  const filtered = useMemo(() => {
    return works.filter((work) => {
      if (filters.types.length > 0 && !filters.types.includes(work.type)) return false;
      if (filters.years.length > 0 && !filters.years.includes(String(work.year))) return false;
      if (
        filters.actressIds.length > 0 &&
        !filters.actressIds.some((id) => work.actressIds.includes(id))
      ) {
        return false;
      }
      return true;
    });
  }, [filters]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    if (sort === "rating") {
      list.sort((a, b) => b.rating - a.rating);
    } else if (sort === "views") {
      list.sort((a, b) => b.views - a.views);
    } else {
      list.sort((a, b) => b.releaseDate.localeCompare(a.releaseDate));
    }
    return list;
  }, [filtered, sort]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const visible = sorted.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const typeOptions = TYPE_OPTIONS.map((type) => ({
    value: type,
    label: type,
    count: works.filter((item) => item.type === type).length,
  }));

  const yearOptions = YEARS.map((year) => ({
    value: String(year),
    label: String(year),
    count: works.filter((item) => item.year === year).length,
  }));

  const selectedCount = filters.types.length + filters.years.length + filters.actressIds.length;

  const filterLabels = [
    ...filters.types,
    ...filters.years.map((year) => `${year} 年`),
    ...filters.actressIds.map(
      (id) => actressOptions.find((option) => option.value === id)?.label ?? "",
    ),
  ].filter(Boolean);

  useSEO({
    title:
      filterLabels.length > 0
        ? `按${filterLabels.slice(0, 2).join("、")}筛选的影视作品 - 日本女优大全`
        : "影视作品列表 - 日本女优电影、电视剧与 OVA 索引",
    description:
      filterLabels.length > 0
        ? `日本女优影视作品列表：当前按${filterLabels.join("、")}筛选，共 ${sorted.length} 部作品，包含类型、上映年份、导演与主演女优信息。`
        : "日本女优影视作品列表页，收录电影、电视剧、OVA 与网络剧，支持按类型、上映年份与主演女优筛选，并按上映时间、评分与热度排序。",
    keywords: "日本女优作品,日本女优影视,日本女优电影,日本女优电视剧,作品列表",
  });

  const toggleFilter = (key: keyof WorkFilterState, value: string) => {
    setFilters((prev) => {
      const current = prev[key];
      const next = current.includes(value)
        ? current.filter((item) => item !== value)
        : [...current, value];
      return { ...prev, [key]: next };
    });
    setPage(1);
  };

  const resetFilters = () => {
    setFilters({ types: [], years: [], actressIds: [] });
    setPage(1);
  };

  return (
    <SiteLayout>
      <Breadcrumbs items={[{ label: "影视作品" }]} />

      <div className="mb-6 border-b border-background-200 pb-5">
        <p className="font-label text-[11px] tracking-[0.2em] text-primary-600">WORKS LIBRARY</p>
        <h1 className="mt-1.5 font-heading text-[26px] font-black leading-tight text-foreground-950 md:text-[32px]">
          影视作品列表
        </h1>
        <p className="mt-2 max-w-3xl text-sm leading-relaxed text-foreground-600">
          本页收录女优出演的电影、电视剧、OVA 与网络剧条目。可按类型、上映年份与主演女优组合筛选，并按上映时间、评分或站内热度排序。作品条目为资料索引，不提供播放与下载。
        </p>
      </div>

      <button
        type="button"
        onClick={() => setMobileOpen((prev) => !prev)}
        aria-expanded={mobileOpen}
        className="mb-3 flex w-full cursor-pointer items-center justify-between rounded-md border border-background-300 bg-background-50 px-4 py-3 font-label text-sm text-foreground-800 md:hidden"
      >
        <span className="flex items-center gap-2">
          <i className="ri-equalizer-line text-primary-600" aria-hidden="true"></i>
          筛选条件
          {selectedCount > 0 ? (
            <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-primary-500 px-1 text-[10px] text-background-50">
              {selectedCount}
            </span>
          ) : null}
        </span>
        <i
          className={`${mobileOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-foreground-500`}
          aria-hidden="true"
        ></i>
      </button>

      <div className={`${mobileOpen ? "block" : "hidden"} md:block`}>
        <WorkFilters
          state={filters}
          onToggle={toggleFilter}
          onReset={resetFilters}
          typeOptions={typeOptions}
          yearOptions={yearOptions}
          actressOptions={actressOptions}
        />
      </div>

      <div className="mb-4 mt-5 flex flex-wrap items-center justify-between gap-3">
        <p className="font-label text-xs text-foreground-500">
          共 <span className="font-medium text-primary-700">{sorted.length}</span> 部作品
          {selectedCount > 0 ? ` · 已应用 ${selectedCount} 个条件` : ""}
        </p>
        <SortSelect
          value={sort}
          options={SORT_OPTIONS}
          onChange={(value) => {
            setSort(value);
            setPage(1);
          }}
        />
      </div>

      {filterLabels.length > 0 ? (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-md bg-background-50 px-3 py-2">
          <span className="font-label text-[11px] text-foreground-500">已选：</span>
          {filterLabels.map((label) => (
            <span
              key={label}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2 py-0.5 font-label text-[11px] text-primary-700"
            >
              {label}
            </span>
          ))}
          <button
            type="button"
            onClick={resetFilters}
            className="ml-1 cursor-pointer font-label text-[11px] text-foreground-500 underline underline-offset-2 hover:text-primary-700"
          >
            清空
          </button>
        </div>
      ) : null}

      {visible.length > 0 ? (
        <>
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-3 xl:grid-cols-4">
            {visible.map((work) => (
              <WorkCard key={work.id} work={work} actressNames={getActressNames(work)} />
            ))}
          </div>
          <Pagination page={currentPage} totalPages={totalPages} onChange={setPage} />
          <p className="mt-4 text-center font-label text-[11px] text-foreground-400">
            每页 12 部 · 当前第 {currentPage} / {totalPages} 页
          </p>
        </>
      ) : (
        <EmptyState
          title="没有符合条件的作品"
          description="当前筛选组合下没有匹配的作品条目。可以尝试减少筛选条件，或重置后浏览全部作品。"
          onReset={resetFilters}
          actionLabel="返回首页"
          actionHref="/"
        />
      )}
    </SiteLayout>
  );
}